import { gql } from '@apollo/client';

export const GetBarnSummaryDataQuery = gql`
  query BarnSummaryTab_GetBarnSummaryData(
    $barnId: uuid!
    $activeFaultWhere: fault_bool_exp!
    $deviceUnreachableFaultCode: Int!
    $feedFrameWhere: feed_frame_bool_exp
    $feedFrameAnalysisWhere: feed_frame_analysis_bool_exp
    $binPredictionCutoff: bigint!
    $feedLineCoefficientWhere: feed_line_coefficient_bool_exp!
    $device_data_start: bigint!
    $algorithmVersionOverride: String
    $hasAlgorithmOverride: Boolean!
  ) {
    farm(where: { id: { _eq: $barnId }, deleted_at: { _is_null: true } }) {
      id
      features_setting: barn_settings(
        where: {
          farm_id: { _eq: $barnId }
          type: { _eq: "features" }
          version: { _eq: 0 }
          deleted_at: { _is_null: true }
        }
      ) {
        settings
      }
      bin_sets(
        where: { deleted_at: { _is_null: true } }
        order_by: [{ bins_aggregate: { min: { name: asc } } }, { created_at: desc }]
      ) {
        id
        bins(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
          id
          capacity_in_grams
          name
          last_delivery: deliveries(
            where: { status: { _eq: "delivered" }, deleted_at: { _is_null: true } }
            order_by: { delivered_at: desc_nulls_last }
            limit: 1
          ) {
            delivered_at
            trip_id
            status
          }
          scheduled_delivery: deliveries(
            where: { status: { _eq: "ordered" }, deleted_at: { _is_null: true } }
            order_by: { ordered_at: asc_nulls_last }
            limit: 1
          ) {
            ordered_at
            trip_id
            status
          }
          recommended_delivery: deliveries(
            where: { status: { _eq: "recomended" }, deleted_at: { _is_null: true } }
            order_by: { ordered_at: asc_nulls_last }
            limit: 1
          ) {
            ordered_at
            trip_id
            status
          }
          shipped_delivery: deliveries(
            where: { status: { _eq: "shipped" }, deleted_at: { _is_null: true } }
            order_by: { ordered_at: asc_nulls_last }
            limit: 1
          ) {
            ordered_at
            trip_id
            status
          }
        }
        bin_set_level_predictions(
          where: {
            version: { _in: ["3.0"] }
            predicted_at: { _gte: $binPredictionCutoff }
            deleted_at: { _is_null: true }
          }
          order_by: { predicted_at: desc }
          limit: 1
        ) {
          bin_set_level_in_grams
          predicted_at
          empty_at
          earliest_delivery_at
          runout_prevention_order_deadline_at
          runout_prevention_bin_set_level_in_grams
          earliest_delivery_bin_set_level_in_grams
          per_bin_level_in_grams
          created_at
        }
        bin_controllers(where: { deleted_at: { _is_null: true } }, limit: 1) {
          id
          device_id
          bin_set_id
          state {
            current_bin_index
            target_bin_index
          }
          bin_controller_bins(where: { deleted_at: { _is_null: true } }) {
            id
            bin_id
            index
            bin {
              id
              name
            }
          }
          device {
            hw_version
            active_device_unreachable_fault: faults(
              where: {
                deleted_at: { _is_null: true }
                ended_at: { _is_null: true }
                code: { _eq: $deviceUnreachableFaultCode }
              }
              limit: 1
            ) {
              id
            }
            active_lockdown_faults: faults(
              where: {
                code: { _in: [2106, 2107, 2115] }
                ended_at: { _is_null: true }
                deleted_at: { _is_null: true }
              }
            ) {
              id
              code
            }
          }
        }
        feed_lines(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
          id
          name
          bin_set_id
          device_assignments(where: { status: { _eq: "active" }, deleted_at: { _is_null: true } }) {
            device {
              id
              serial
              device_data_aggregate(where: { started_at: { _gte: $device_data_start } }) {
                aggregate {
                  sum {
                    ended_at
                    created_at
                    duration
                  }
                  count
                }
              }
              feed_frames(where: $feedFrameWhere, order_by: [{ started_at: desc_nulls_last }, { id: asc }], limit: 1) {
                id
                started_at
                ended_at
                duration
                feed_frame_analyses(
                  where: $feedFrameAnalysisWhere
                  order_by: { created_at: desc_nulls_last }
                  limit: 1
                ) {
                  latest_estimated_mass_moved_in_grams
                }
              }
              active_device_unreachable_fault: faults(
                where: {
                  deleted_at: { _is_null: true }
                  ended_at: { _is_null: true }
                  code: { _eq: $deviceUnreachableFaultCode }
                }
                limit: 1
              ) {
                id
              }
              most_recent_transaction: transactions(
                where: { deleted_at: { _is_null: true } }
                order_by: { occured_at: desc_nulls_last }
                limit: 1
              ) {
                occured_at
              }
              active_alertable_fault_codes: faults(distinct_on: code, where: $activeFaultWhere) {
                id
                code
                # Use computed field when no override (handles all the DB logic)
                nofeed_seconds @skip(if: $hasAlgorithmOverride)
                # When override is set, query the nofeed_times directly and compute client-side
                fault_nofeed_times(
                  where: { algorithm_version: { _eq: $algorithmVersionOverride } }
                  limit: 1
                  order_by: { created_at: desc }
                ) @include(if: $hasAlgorithmOverride) {
                  nofeed_time_in_seconds
                }
              }
            }
          }
          bin_set {
            tasks(
              limit: 1
              order_by: { task: { due_at: desc } }
              where: { task: { completed_at: { _is_null: true }, deleted_at: { _is_null: true } } }
            ) {
              task {
                due_at
                remind_at
                completed_at
              }
            }
          }
          feed_line_coefficients(
            limit: 1
            order_by: { created_at: desc }
            where: { _and: [$feedLineCoefficientWhere, { deleted_at: { _is_null: true } }] }
          ) {
            created_at
          }
        }
      }
    }
  }
`;

export const CREATE_SET_ACTIVE_BIN_COMMAND = gql`
  mutation CreateSetActiveBinCommand($deviceId: uuid!, $binIndex: Int!) {
    insert_device_command_one(object: { device_id: $deviceId, type: "Set Active Bin", data: { bin_index: $binIndex } }) {
      id
      created_at
      sent_at
      acknowledged_at
    }
  }
`;

export const CREATE_LOCKDOWN_BINS_COMMAND = gql`
  mutation CreateLockdownBinsCommand($deviceId: uuid!) {
    insert_device_command_one(object: { device_id: $deviceId, type: "Lockdown Bins", data: {} }) {
      id
      created_at
      sent_at
      acknowledged_at
    }
  }
`;

export const BIN_CHANGE_SUBSCRIPTION = gql`
  subscription BinChangeSubscription($deviceId: uuid!) {
    fault(
      where: { device_id: { _eq: $deviceId }, code: { _eq: 2101 }, deleted_at: { _is_null: true } }
      order_by: [{ started_at: desc }, { order: asc }]
      limit: 1
    ) {
      id
      started_at
      details
    }
  }
`;

export const LOCKDOWN_FAULT_SUBSCRIPTION = gql`
  subscription LockdownFaultSubscription($deviceId: uuid!) {
    fault(
      where: {
        device_id: { _eq: $deviceId }
        code: { _in: [2106, 2107, 2115] }
        ended_at: { _is_null: true }
        deleted_at: { _is_null: true }
      }
    ) {
      id
      code
    }
  }
`;
